import { computed, onMounted, ref, watch } from 'vue';
import NucleoWorkOrdersAPI from 'dashboard/api/nucleoWorkOrders';

const FILTERS_KEY = 'nucleo_work_orders_filters';
const TAB_KEY = 'nucleo_work_orders_tab';
const DEFAULT_FILTERS = {
  filtro_endereco: '',
  filtro_responsavel: [],
  filtro_gerente: [],
  filtro_solicitacao_onus: [],
  filtro_certidoes_comarca: [],
  filtro_loja: [],
  ordenacao: 'data_solicitacao_1',
};

const storedValue = (key, fallback) => {
  try {
    return JSON.parse(window.localStorage.getItem(key) || 'null') ?? fallback;
  } catch {
    return fallback;
  }
};

export function useNucleoWorkOrders({ notify, t = key => key } = {}) {
  const tab = ref(storedValue(TAB_KEY, 'andamento'));
  const filters = ref({ ...DEFAULT_FILTERS, ...storedValue(FILTERS_KEY, {}) });
  const catalog = ref({
    addresses: [],
    responsibles: [],
    managers: [],
    onus_requests: [],
    district_certificates: [],
    stores: [],
    orders: [],
  });
  const counts = ref({ andamento: 0, finalizados: 0, novos: 0 });
  const rows = ref([]);
  const page = ref(1);
  const meta = ref({ count: 0, current_page: 1, per_page: 25, total_pages: 0 });
  const loading = ref(false);
  const loadingCatalog = ref(false);
  const error = ref('');
  const lastUpdatedAt = ref(null);

  const totalPages = computed(() => meta.value.total_pages || 0);
  const requestParams = () => ({
    ...filters.value,
    tab: tab.value,
    page: page.value,
    per_page: meta.value.per_page,
  });
  const notifyError = message => {
    error.value = message;
    if (notify) notify(message, 'error');
  };

  const load = async () => {
    loading.value = true;
    error.value = '';
    try {
      const [listResponse, countsResponse] = await Promise.all([
        NucleoWorkOrdersAPI.list(requestParams()),
        NucleoWorkOrdersAPI.counts({ ...filters.value, tab: tab.value }),
      ]);
      rows.value = listResponse.data?.data || [];
      meta.value = { ...meta.value, ...(listResponse.data?.meta || {}) };
      counts.value = { ...counts.value, ...(countsResponse.data?.data || {}) };
      lastUpdatedAt.value = new Date();
    } catch {
      notifyError(t('NUCLEO_WORK_ORDERS.ERRORS.LOAD'));
    } finally {
      loading.value = false;
    }
  };

  const loadCatalog = async () => {
    loadingCatalog.value = true;
    try {
      const response = await NucleoWorkOrdersAPI.filterCatalog(requestParams());
      catalog.value = { ...catalog.value, ...(response.data?.data || {}) };
    } catch {
      notifyError(t('NUCLEO_WORK_ORDERS.ERRORS.CATALOG'));
    } finally {
      loadingCatalog.value = false;
    }
  };

  const markViewed = async (listingId, orderId) => {
    if (!listingId || !orderId) return;

    try {
      await NucleoWorkOrdersAPI.markViewed(listingId, orderId);
    } catch (requestError) {
      notifyError(t('NUCLEO_WORK_ORDERS.ERRORS.VIEW'));
      throw requestError;
    }
  };

  const setTab = value => {
    tab.value = value;
    page.value = 1;
    window.localStorage.setItem(TAB_KEY, JSON.stringify(value));
  };

  const applyFilters = value => {
    filters.value = { ...DEFAULT_FILTERS, ...value };
    page.value = 1;
    window.localStorage.setItem(FILTERS_KEY, JSON.stringify(filters.value));
  };

  const clearFilters = () => applyFilters({});
  const changePage = value => {
    page.value = Math.max(1, value);
  };
  const refresh = () => Promise.all([load(), loadCatalog()]);

  onMounted(refresh);
  watch([tab, filters, page], load, { deep: true });
  watch(tab, loadCatalog);

  return {
    tab,
    filters,
    catalog,
    counts,
    rows,
    page,
    meta,
    loading,
    loadingCatalog,
    error,
    lastUpdatedAt,
    totalPages,
    load,
    refresh,
    markViewed,
    setTab,
    applyFilters,
    clearFilters,
    changePage,
  };
}
